import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetProjectPrimaryDetailsById } from "../../../../../api/services/project/projectApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../api/services/user/userApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiButtonTertiary,
  MuiTypography,
  StatusBadge,
  StatusBadgeType,
} from "../../../../../components";
import PermissionWrapper from "../../../../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS, ROLE_IDS } from "../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../helpers/constants/componentIds";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import {
  getValueOrBlank,
  mapProjectFacilitatorToTableData,
  mapSaleCreditToTableData,
} from "../../../../../helpers/utils";
import useAccordion from "../../../../../hooks/useAccordion";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { ProjectActivityAndDeliverableMain } from "../../../../createProject/components/createProjectTabs/tabs/project/components/activitiesAndDeliverables/ProjectActivityAndDeliverableMain";
import { activitiesListSiteCoreKeys } from "../../../../createProject/components/createProjectTabs/tabs/project/components/activitiesAndDeliverables/siteCore/SiteCoreKeys";
import ActivityTimeline from "../../../../createProject/components/createProjectTabs/tabs/project/components/activityTimeline/ActivityTimeline";
import { FacilitatorList } from "../../../../createProject/components/createProjectTabs/tabs/project/components/facilitator/components/facilitatorList/FacilitatorList";
import { facilitatorSiteCoreKeys } from "../../../../createProject/components/createProjectTabs/tabs/project/components/facilitator/SiteCoreKeys";
import { ProjectStatusResult } from "../../../../createProject/components/createProjectTabs/tabs/project/components/projectStatus/ProjectStatusResult";
import { ProjectSalesCreditDetails } from "../../../../createProject/components/createProjectTabs/tabs/project/components/salesCredit/SalesCreditDetails";
import { ProjectTabFormKey } from "../../../../createProject/modelsAndSchemas/Models";
import {
  activityTimelineSiteCoreKeys,
  projectStatusSiteCoreKeys,
  salesCreditSiteCoreKeys,
} from "../../../../createProject/siteCore/SiteCoreKeys";
import { pageId } from "../../../../createUser/CreateUser";
import { mapProjectStatusHistoryToTableData } from "../../helper";
import ViewOfficeAndContactDetails from "../viewOfficeAndContactDetails/ViewOfficeAndContactDetails";
import { ViewProjectContainer } from "./ViewProject.style";

const initialAccordionStatus = {
  officeAndContactDetails: true,
  projectStatus: false,
  salesCredit: false,
  facilitator: false,
  activitiesAndDeliverables: false,
  activityTimeline: false,
};

const ProjectTabView: React.FC = () => {
  const { setValue } = useFormContext();
  const {
    commonLabels,
    fetchComponentTitle,
    fetchLabel,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { id } = useParams();
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const {
    data: projectPrimaryDetails,
    refetchWithParams: refetchProjectPrimaryDetails,
  } = useGetProjectPrimaryDetailsById();

  const getRemoveIcon = () => <RemoveIcon color="primary" />;
  const getAddIcon = () => <AddIcon color="primary" />;

  useEffect(() => {
    refetchProjectPrimaryDetails(id || "");
  }, [id]);

  const salesCredit = projectPrimaryDetails?.salesCredit;

  const {
    mutateAsync: getClientRelationshipOwners,
    data: clientRelationshipOwnerList,
  } = useGetUsersDetailByRoleMutation();

  const projectStatusRows = mapProjectStatusHistoryToTableData(
    projectPrimaryDetails?.projectStatus?.projectStatusHistoryResponse ?? [],
  );

  const projectCurrentStatus =
    projectPrimaryDetails?.projectStatus?.currentStatus;
  const officeAndContactDetails =
    projectPrimaryDetails?.officeAndContactDetails;

  const salesCreditRow = mapSaleCreditToTableData(
    salesCredit ? salesCredit : [],
    true,
  );

  useEffect(() => {
    getClientRelationshipOwners({ roleIds: [ROLE_IDS.sales] });
  }, []);

  useEffect(() => {
    setValue(ProjectTabFormKey.salesCreditDetails, salesCreditRow);
  }, [salesCreditRow]);

  const facilitatorList = projectPrimaryDetails?.projectFacilitator
    ? mapProjectFacilitatorToTableData(
        projectPrimaryDetails?.projectFacilitator
          ?.projectFacilitatorTypeAssignments,
        fetchValueByKeyOfDropdown,
        getValueOrBlank(userCountryDateFormat),
      )
    : [];

  return (
    <ViewProjectContainer>
      <MuiBox>
        <MuiBox className="header-collapse" id={`${pageId}-view-program`}>
          <MuiButtonTertiary
            id={COMPONENT_IDS.EXPAND_COLLAPSE_BUTTON}
            onClick={handleExpandCollapseAllChange}
          >
            {accordionStatus.all
              ? commonLabels.collapseAll
              : commonLabels.expandAll}
            {accordionStatus.all ? getRemoveIcon() : getAddIcon()}
          </MuiButtonTertiary>
        </MuiBox>
        <MuiBox className="office-and-contact-details">
          <PermissionWrapper
            permission={
              MODULE_PERMISSIONS.PROJECT_VIEW_OFFICE_AND_CONTACT_DETAILS
            }
          >
            <ViewOfficeAndContactDetails
              officeAndContactDetails={officeAndContactDetails}
              handleIndividualAccordionChange={handleIndividualAccordionChange}
              isExpanded={accordionStatus.officeAndContactDetails}
            />
          </PermissionWrapper>
        </MuiBox>
        <PermissionWrapper
          permission={MODULE_PERMISSIONS.PROJECT_VIEW_PROJECT_STATUS}
        >
          <AccordionHeader
            wrapperClassName="accordion"
            title={
              <AccordionHeaderText
                label={fetchComponentTitle(
                  projectStatusSiteCoreKeys.projectStatus,
                )}
              />
            }
            onChange={() => handleIndividualAccordionChange("projectStatus")}
            isExpanded={accordionStatus.projectStatus}
          >
            <MuiBox className="status-label">
              <MuiTypography variant="h3" display="block">
                {fetchLabel(
                  projectStatusSiteCoreKeys.projectStatus,
                  projectStatusSiteCoreKeys.currentStatus,
                )}
              </MuiTypography>

              <StatusBadge
                status={projectCurrentStatus as StatusBadgeType}
                label={fetchValueByKeyOfDropdown(
                  projectStatusSiteCoreKeys.projectStatus,
                  projectStatusSiteCoreKeys.statusList,
                  projectCurrentStatus as string,
                )}
              />
            </MuiBox>
            <ProjectStatusResult rows={projectStatusRows} pageId={pageId} />
          </AccordionHeader>
        </PermissionWrapper>
        <PermissionWrapper
          permission={MODULE_PERMISSIONS.PROJECT_VIEW_SALES_CREDIT}
        >
          <AccordionHeader
            wrapperClassName="accordion view-sales-credit"
            title={
              <AccordionHeaderText
                label={fetchComponentTitle(salesCreditSiteCoreKeys.salesCredit)}
              />
            }
            onChange={() => handleIndividualAccordionChange("salesCredit")}
            isExpanded={accordionStatus.salesCredit}
          >
            <ProjectSalesCreditDetails
              pageId={pageId}
              isInViewMode={true}
              resultsFormKey={ProjectTabFormKey.salesCreditDetails}
              clientRelationshipOwnerList={clientRelationshipOwnerList ?? []}
            />
          </AccordionHeader>
        </PermissionWrapper>
        <PermissionWrapper
          permission={MODULE_PERMISSIONS.PROJECT_VIEW_FACILITATOR}
        >
          <AccordionHeader
            wrapperClassName="accordion"
            title={
              <AccordionHeaderText
                label={fetchComponentTitle(facilitatorSiteCoreKeys.facilitator)}
              />
            }
            onChange={() => handleIndividualAccordionChange("facilitator")}
            isExpanded={accordionStatus.facilitator}
          >
            <FacilitatorList
              facilitatorList={facilitatorList}
              onActionClick={() => {}}
              dateFormat={userCountryDateFormat as string}
              isViewOnly
            />
          </AccordionHeader>
        </PermissionWrapper>
        <PermissionWrapper
          permission={
            MODULE_PERMISSIONS.PROJECT_VIEW_ACTIVITIES_AND_DELIVERABLES
          }
        >
          <AccordionHeader
            wrapperClassName="accordion"
            title={
              <AccordionHeaderText
                label={fetchComponentTitle(
                  activitiesListSiteCoreKeys.activitiesList,
                )}
              />
            }
            onChange={() =>
              handleIndividualAccordionChange("activitiesAndDeliverables")
            }
            isExpanded={accordionStatus.activitiesAndDeliverables}
          >
            <ProjectActivityAndDeliverableMain isAddButtonVisible={false} />
          </AccordionHeader>
        </PermissionWrapper>
        <PermissionWrapper
          permission={MODULE_PERMISSIONS.PROJECT_VIEW_ACTIVITY_TIMELINE}
        >
          <AccordionHeader
            wrapperClassName="accordion"
            title={
              <AccordionHeaderText
                label={fetchComponentTitle(
                  activityTimelineSiteCoreKeys.activityTimeline,
                )}
              />
            }
            onChange={() => handleIndividualAccordionChange("activityTimeline")}
            isExpanded={accordionStatus.activityTimeline}
          >
            <ActivityTimeline
              pageId={pageId}
              viewOnly
              projectId={id || ""}
              displayAddBtn={false}
            />
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>
    </ViewProjectContainer>
  );
};

export default ProjectTabView;
